import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  SelectChangeEvent,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Webhook,
  Home,
  AdminPanelSettings,
  Refresh,
  FilterList,
  Clear,
  Receipt,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface WebhookLog {
  id: number;
  eventId: string;
  eventType: string;
  realmId: string;
  integrationType: 'horwath' | 'barrett' | null;
  entityName: string;
  entityId: string;
  operation: string;
  eventTime: string;
  processedAt: string;
  createdAt: string;
  processingStatus: 'success' | 'failed' | 'skipped' | 'no_handler' | null;
}

interface WebhookLogsResponse {
  success: boolean;
  data: {
    logs: WebhookLog[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
}

const ENTITY_NAMES = [
  { value: '', label: 'All Entities' },
  { value: 'Bill', label: 'Bill' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Payment', label: 'Payment' },
];

const OPERATIONS = [
  { value: '', label: 'All Operations' },
  { value: 'Create', label: 'Create' },
  { value: 'Update', label: 'Update' },
  { value: 'Delete', label: 'Delete' },
  { value: 'Void', label: 'Void' },
];

const PROCESSING_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'no_handler', label: 'No Handler' },
];

const INTEGRATION_TYPES = [
  { value: '', label: 'All Integrations' },
  { value: 'horwath', label: 'Horwath' },
  { value: 'barrett', label: 'Barrett' },
];

export const QuickBooksWebhookLogsPage: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();

  // Filter state
  const [entityName, setEntityName] = useState('');
  const [operation, setOperation] = useState('');
  const [processingStatus, setProcessingStatus] = useState('');
  const [integrationType, setIntegrationType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  // Build query params
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationModel.page + 1));
    params.append('pageSize', String(paginationModel.pageSize));
    if (entityName) params.append('entityName', entityName);
    if (operation) params.append('operation', operation);
    if (processingStatus) params.append('processingStatus', processingStatus);
    if (integrationType) params.append('integrationType', integrationType);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return params.toString();
  };

  // Fetch webhook logs
  const { data: logsData, isLoading, refetch } = useQuery<WebhookLogsResponse>({
    queryKey: ['quickbooks-webhook-logs', entityName, operation, processingStatus, integrationType, startDate, endDate, paginationModel],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/quickbooks-webhook-logs/portal/logs?${buildQueryParams()}`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch webhook logs');
      }
      return await response.json();
    },
    enabled: !!user?.permissions?.includes('permissions.all'),
  });

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have permissions.all permission
  if (!user?.permissions?.includes('permissions.all')) {
    return <Navigate to="/" replace />;
  }

  const logs = logsData?.data?.logs || [];
  const pagination = logsData?.data?.pagination || { page: 1, pageSize: 50, total: 0, totalPages: 0 };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatExactTime = (dateString: string) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  const getOperationColor = (op: string) => {
    switch (op) {
      case 'Create':
        return 'success';
      case 'Update':
        return 'info';
      case 'Delete':
        return 'error';
      case 'Void':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getProcessingStatusColor = (status: string | null) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'skipped':
        return 'warning';
      case 'no_handler':
        return 'default';
      default:
        return 'default';
    }
  };

  const getProcessingStatusLabel = (status: string | null) => {
    switch (status) {
      case 'success':
        return 'Success';
      case 'failed':
        return 'Failed';
      case 'skipped':
        return 'Skipped';
      case 'no_handler':
        return 'No Handler';
      default:
        return '-';
    }
  };

  const handleClearFilters = () => {
    setEntityName('');
    setOperation('');
    setProcessingStatus('');
    setIntegrationType('');
    setStartDate('');
    setEndDate('');
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const getIntegrationTypeColor = (type: string | null) => {
    switch (type) {
      case 'horwath':
        return 'primary';
      case 'barrett':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getIntegrationTypeLabel = (type: string | null) => {
    switch (type) {
      case 'horwath':
        return 'Horwath';
      case 'barrett':
        return 'Barrett';
      default:
        return 'Unknown';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'integrationType',
      headerName: 'Integration',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getIntegrationTypeLabel(params.value as string | null)}
          size="small"
          color={getIntegrationTypeColor(params.value as string | null) as any}
          variant="filled"
        />
      ),
    },
    {
      field: 'entityName',
      headerName: 'Entity',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      ),
    },
    {
      field: 'entityId',
      headerName: 'Entity ID',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'operation',
      headerName: 'Operation',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={getOperationColor(params.value as string) as any}
          variant="outlined"
        />
      ),
    },
    {
      field: 'processingStatus',
      headerName: 'Status',
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Chip
            label={getProcessingStatusLabel(params.value as string)}
            size="small"
            color={getProcessingStatusColor(params.value as string) as any}
            variant="filled"
          />
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )
      ),
    },
    {
      field: 'eventTime',
      headerName: 'Relative Time',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatRelativeTime(params.value as string)}
        </Typography>
      ),
    },
    {
      field: 'eventTimeExact',
      headerName: 'Exact Time',
      width: 170,
      valueGetter: (value: any, row: WebhookLog) => row.eventTime,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {formatExactTime(params.value as string)}
        </Typography>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ pt: 4, pb: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Receipt sx={{ mr: 0.5 }} fontSize="inherit" />
            QuickBooks Webhook Logs
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Receipt sx={{ fontSize: 48, color: '#2CA01C' }} />
            <Typography variant="h4" component="h1" sx={{ color: '#607D8B' }}>
              QuickBooks Webhook Logs
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            View and analyze QuickBooks webhook events received by the system
          </Typography>
        </Box>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FilterList sx={{ color: '#607D8B' }} />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Entity</InputLabel>
              <Select
                value={entityName}
                label="Entity"
                onChange={(e: SelectChangeEvent) => {
                  setEntityName(e.target.value);
                  setPaginationModel({ ...paginationModel, page: 0 });
                }}
              >
                {ENTITY_NAMES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Operation</InputLabel>
              <Select
                value={operation}
                label="Operation"
                onChange={(e: SelectChangeEvent) => {
                  setOperation(e.target.value);
                  setPaginationModel({ ...paginationModel, page: 0 });
                }}
              >
                {OPERATIONS.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={processingStatus}
                label="Status"
                onChange={(e: SelectChangeEvent) => {
                  setProcessingStatus(e.target.value);
                  setPaginationModel({ ...paginationModel, page: 0 });
                }}
              >
                {PROCESSING_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Integration</InputLabel>
              <Select
                value={integrationType}
                label="Integration"
                onChange={(e: SelectChangeEvent) => {
                  setIntegrationType(e.target.value);
                  setPaginationModel({ ...paginationModel, page: 0 });
                }}
              >
                {INTEGRATION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Start Date"
              type="datetime-local"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              label="End Date"
              type="datetime-local"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={handleClearFilters}
                sx={{ color: '#607D8B' }}
              >
                <Clear />
              </IconButton>
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            <Chip
              label={`${pagination.total} Total Events`}
              sx={{
                bgcolor: '#2CA01C',
                color: '#FFFFFF',
                fontWeight: 600,
              }}
            />
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetch()}
                sx={{ color: '#607D8B' }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Data Grid */}
        <Paper elevation={2} sx={{ flex: 1, minHeight: 400, mb: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
              <Alert severity="info" sx={{ maxWidth: 400 }}>
                No webhook logs found matching the current filters.
              </Alert>
            </Box>
          ) : (
            <DataGrid
              rows={logs}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={pagination.total}
              paginationMode="server"
              sortingMode="server"
              initialState={{
                sorting: {
                  sortModel: [{ field: 'id', sort: 'desc' }],
                },
              }}
              disableRowSelectionOnClick
              onRowClick={(params) => navigate(`/admin/quickbooks-webhook-logs/${params.row.id}`)}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                },
              }}
            />
          )}
        </Paper>
      </Box>
    </Container>
  );
};
